import React from 'react'
import { useFormContext } from 'react-hook-form'
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import EventIcon from 'components/material-symbol/icons/Event'
import CollectionsBookmarkIcon from 'components/material-symbol/icons/CollectionsBookmark'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import AccountRouterLink from 'components/account-router-link'
import { useStyles as useEditFormStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import OfferingTypeCardGroup from './offering-type-card-group'

const LESSON_PACK_LEGAL_CAPTION =
  "The ability to sell lesson packs through PGA Coach is offered to coaches as a convenience. It is the individual coach's responsibility to establish the terms and conditions of such sales and ensure that the sale of lesson packs is compliant with applicable law."

const LESSON_PACK_PAYMENT_CAPTION =
  'Heads up! Offering lesson packs requires you have an online payment account set up. Get set up with online payments now.'

const LessonPackLegalTip = () => (
  <Box mt={2}>
    <TipWell
      variant="neutral"
      icon={InfoOutlinedIcon}
      caption={LESSON_PACK_LEGAL_CAPTION}
    />
  </Box>
)

const LessonPackPaymentAccountTip = () => (
  <Box mt={2}>
    <TipWell
      icon={InfoOutlinedIcon}
      caption={LESSON_PACK_PAYMENT_CAPTION}
      action={
        <TipWellActionButton
          component={AccountRouterLink}
          to="/account/payments/payment-accounts"
        >
          Add a Payment Account
        </TipWellActionButton>
      }
    />
  </Box>
)

const OFFERING_TYPE_OPTIONS = [
  {
    value: 'bookingType',
    title: 'Booking type',
    icon: EventIcon,
    description:
      'Online or offline payments. Best for one-time lessons. Students book at time of purchase.',
  },
  {
    value: 'lessonPack',
    title: 'Lesson pack',
    icon: CollectionsBookmarkIcon,
    description:
      'Online payments required. Best for multi-session coaching. Offer different quantity packages. Students can purchase now and book later.',
  },
]

const VIRTUAL_COACHING_OPTION = {
  value: 'virtualCoaching',
  title: 'Virtual coaching',
  icon: VideocamIcon,
  description:
    'Online payments required. Best for swing analysis. Students buy, upload a video, and you send back video feedback on your own schedule.',
}

const SingleOrLessonPack = ({
  isLessonPacks,
  setIsLessonPacks,
  isVirtualCoaching = false,
  setIsVirtualCoaching,
  showVirtualCoaching = false,
  paymentAccounts,
  isEditFlow = false,
  readOnly = false,
}) => {
  const editClasses = useEditFormStyles()
  const { setValue } = useFormContext()

  let selectedValue = isLessonPacks ? 'lessonPack' : 'bookingType'
  if (isVirtualCoaching) selectedValue = 'virtualCoaching'

  const offeringTypeOptions =
    showVirtualCoaching || isVirtualCoaching
      ? [...OFFERING_TYPE_OPTIONS, VIRTUAL_COACHING_OPTION]
      : OFFERING_TYPE_OPTIONS

  const handleOfferingTypeChange = event => {
    if (readOnly) return
    const nextIsVirtualCoaching = event.target.value === 'virtualCoaching'
    const nextIsLessonPacks = event.target.value === 'lessonPack'
    setIsLessonPacks(nextIsLessonPacks)
    setValue('isLessonPacks', nextIsLessonPacks, {
      shouldValidate: true,
      shouldDirty: true,
    })
    if (setIsVirtualCoaching) {
      setIsVirtualCoaching(nextIsVirtualCoaching)
      setValue('isVirtualCoaching', nextIsVirtualCoaching, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }

  if (isEditFlow) {
    return (
      <Box className={editClasses.fieldGroup}>
        <Typography
          id="offering-type-heading"
          className={editClasses.sectionHeading}
        >
          Offering Type
        </Typography>
        <Typography
          id="offering-type-helper"
          className={editClasses.fieldHelper}
        >
          {readOnly
            ? 'This offering is configured as one of the types below.'
            : 'Select whether you want to create a single booking type or a pack of multiple lessons.'}
        </Typography>
        <FormControl component="fieldset" fullWidth>
          <OfferingTypeCardGroup
            options={offeringTypeOptions}
            value={selectedValue}
            onChange={handleOfferingTypeChange}
            readOnly={readOnly}
            labelledBy="offering-type-heading"
            describedBy="offering-type-helper"
          />
        </FormControl>
        {isLessonPacks ? <LessonPackLegalTip /> : null}
        {isLessonPacks && paymentAccounts.length === 0 ? (
          <LessonPackPaymentAccountTip />
        ) : null}
        {isVirtualCoaching && paymentAccounts.length === 0 ? (
          <LessonPackPaymentAccountTip />
        ) : null}
      </Box>
    )
  }

  return (
    <Box mb={3}>
      <Typography variant="body2" gutterBottom color="textSecondary">
        Select whether you want to create a single booking type or pack of
        multiple lessons.
      </Typography>
      <FormControl>
        <RadioGroup aria-labelledby="Lesson Type">
          <FormControlLabel
            checked={!isLessonPacks}
            control={<Radio color="primary" />}
            label={
              <Box ml={2}>
                <Typography variant="subtitle1">Booking Type</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Online or Offline Payments
                  <br />
                  Best for One-Time Lesson
                  <br />
                  Students Book at Time of Purchase
                </Typography>
              </Box>
            }
            data-cy="single"
            onClick={() => {
              setIsLessonPacks(false)
              setValue('isLessonPacks', false, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
          <FormControlLabel
            checked={isLessonPacks}
            control={<Radio color="primary" />}
            label={
              <Box ml={2}>
                <Typography variant="subtitle1">Lesson Pack</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Online Payments Required
                  <br />
                  Best for Multi-Session Coaching
                  <br />
                  Offer Different Quantity Packages
                  <br />
                  Students can Purchase Now, Book Later
                </Typography>
              </Box>
            }
            data-cy="lessonPack"
            onClick={() => {
              setIsLessonPacks(true)
              setValue('isLessonPacks', true, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
          />
        </RadioGroup>
      </FormControl>
      {isLessonPacks ? <LessonPackLegalTip /> : null}
      {isLessonPacks && paymentAccounts.length === 0 ? (
        <LessonPackPaymentAccountTip />
      ) : null}
    </Box>
  )
}

export default SingleOrLessonPack
